// src/components/cooler/inventory/components/AddItemDialog.tsx

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Autocomplete,
  FormControlLabel,
  Switch,
  InputAdornment,
  Typography,
  Chip,
  Paper,
  Collapse,
  Fade,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Tag as CategoryIcon,
  MapPin as LocationIcon,
  Euro,
  Calendar,
  Info as InfoIcon,
  TagIcon,
  Building as BusinessIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Circle as UnfilledIcon,
  Power,
  X as CloseIcon,
} from 'lucide-react';

import { AtomicUpload } from '../../shared/AtomicUpload';
import {
  CATEGORIES,
  LOCATIONS,
  SecondLocation,
  VENDORS,
  shouldShowSerialNumber,
  type Category,
  type Location,
  type Vendor
} from '../constants/inventoryConstants';
import { 
    hasSecondLocation, 
    getSecondLocations,
    getValidAlternateLocations 
  } from '../utils/locationHelpers';
import type { NewInventoryItem } from '../types/inventoryTypes';
import { SmartNameField } from './SmartNameField';
import { PRODUCTION_ITEMS } from '../constants/NamesConstants';
import axiosInstance from '../../../utils/axiosConfig';

interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (item: NewInventoryItem & { selectedImageFiles?: File[], selectedProofFile?: File[] }) => void;
}

const SectionHeader: React.FC<{ 
  icon: React.ReactNode; 
  title: string; 
  completed?: boolean;
  onClick?: () => void;
  collapsible?: boolean;
}> = ({ 
  icon, 
  title, 
  completed = false,
  onClick,
  collapsible = false
}) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1, 
    mb: 2, 
    p: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    cursor: collapsible ? 'pointer' : 'default',
    userSelect: 'none',
    '&:hover': collapsible ? {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    } : {}
  }}
  onClick={onClick}
  >
    {icon}
    <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
      {title}
    </Typography>
    {completed ? (
      <CheckCircleIcon size={20} color="green" />
    ) : (
      <UnfilledIcon size={20} color="gray" />
    )}
  </Box>
);

export const AddItemDialog: React.FC<AddItemDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [newItem, setNewItem] = useState<NewInventoryItem>({
    _id: undefined,
    name: '',
    description: '',
    category: 'Sound' as Category,
    primaryLocation: 'Main Press Left' as Location,
    secondLocation: undefined,
    secondaryLocation: undefined,
    serialNumber: '',
    purchaseDate: '',
    purchaseValue: 0,
    currentValue: 0,
    vendor: undefined,
    requiresPower: false,
    powerLocation: undefined,
    relatedEquipment: [],
    quantity: 1,
    tags: [],
    notes: '',
    images: [],
    isInsured: false,
  });

  const [availableSecondLocations, setAvailableSecondLocations] = useState<string[]>([]);
  const [availableSecondaryLocations, setAvailableSecondaryLocations] = useState<Location[]>([]);
  const [selectedImageFiles, setSelectedImageFiles] = useState<File[]>([]);
  const [selectedProofFile, setSelectedProofFile] = useState<File[]>([]);
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper to check completion status
  const isBasicComplete = Boolean(newItem.name && newItem.category);
  const isLocationComplete = Boolean(newItem.primaryLocation);
  const hasPurchaseInfo = Boolean(newItem.purchaseDate || newItem.vendor || newItem.purchaseValue);
  const hasTechnicalInfo = Boolean(newItem.serialNumber || newItem.requiresPower || newItem.tags?.length);

  // Fetch existing tags when dialog opens
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axiosInstance.get('/api/inventory/tags');
        setExistingTags(response.data);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };
    
    if (open) {
      fetchTags();
    }
  }, [open]);

  // Update available locations when primary location changes
  useEffect(() => {
    if (hasSecondLocation(newItem.primaryLocation)) {
      setAvailableSecondLocations(getSecondLocations(newItem.primaryLocation));
    } else {
      setAvailableSecondLocations([]);
      setNewItem(prev => ({
        ...prev,
        secondLocation: undefined
      }));
    }

    setAvailableSecondaryLocations(getValidAlternateLocations(newItem.primaryLocation));
  }, [newItem.primaryLocation]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setNewItem({
        _id: undefined,
        name: '',
        description: '',
        category: 'Sound' as Category,
        primaryLocation: 'Main Press Left' as Location,
        secondLocation: undefined,
        secondaryLocation: undefined,
        serialNumber: '',
        purchaseDate: '',
        purchaseValue: 0,
        currentValue: 0,
        vendor: undefined,
        requiresPower: false,
        powerLocation: undefined,
        relatedEquipment: [],
        quantity: 1,
        tags: [],
        notes: '',
        images: [],
        isInsured: false,
      });
      setSelectedImageFiles([]);
      setSelectedProofFile([]);
      setShowAdvanced(false);
      setShowTechnical(false);
    }
  }, [open]);

  const handlePrimaryLocationChange = (value: Location) => {
    setNewItem(prev => ({
      ...prev,
      primaryLocation: value,
      secondLocation: undefined,
      secondaryLocation: prev.secondaryLocation === value ? undefined : prev.secondaryLocation
    }));
  };

  // Auto-uppercase handler for serial numbers
  const handleSerialNumberChange = (value: string) => {
    setNewItem({ ...newItem, serialNumber: value.toUpperCase() });
  };

  const handleSave = () => {
    const itemWithFiles = {
      ...newItem,
      selectedImageFiles,
      selectedProofFile,
    };
    
    onSave(itemWithFiles);
  };

  const toTitleCase = (str: string) => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Add New Equipment</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: isMobile ? 1 : 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3, pt: 2 }}>
          
          {/* Images Section - FIRST for mobile workflow */}
          <Paper sx={{ 
            p: isMobile ? 2 : 3, 
            backgroundColor: 'rgba(63, 81, 181, 0.02)',
            border: isMobile ? '2px dashed rgba(63, 81, 181, 0.3)' : 'none'
          }}>
            <SectionHeader 
              icon={<UploadIcon size={20} />} 
              title={isMobile ? "Add Photos First (Optional)" : "Images & Documentation (Optional)"}
            />
            
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ 
                  fontSize: isMobile ? '0.95rem' : '0.875rem',
                  fontWeight: isMobile ? 600 : 500 
                }}>
                  Equipment Photos
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block', 
                  mb: 1,
                  fontSize: isMobile ? '0.8rem' : '0.75rem'
                }}>
                  {isMobile ? "Snap a quick photo to get started!" : "Upload images of the equipment"}
                </Typography>
                <AtomicUpload
                  onFilesReady={(files) => setSelectedImageFiles(files)}
                  maxFiles={5}
                  accept="image/*"
                  type="images"
                  label={isMobile ? "Take Photo" : "Select Images"}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ 
                  fontSize: isMobile ? '0.95rem' : '0.875rem',
                  fontWeight: isMobile ? 600 : 500 
                }}>
                  Receipt/Invoice
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block', 
                  mb: 1,
                  fontSize: isMobile ? '0.8rem' : '0.75rem'
                }}>
                  {isMobile ? "Got a receipt? Add it here" : "Upload proof of purchase"}
                </Typography>
                <AtomicUpload
                  onFilesReady={(files) => setSelectedProofFile(files)}
                  maxFiles={1}
                  accept=".pdf,image/*"
                  type="documents"
                  label={isMobile ? "Add Receipt" : "Select Proof"}
                />
              </Grid>
            </Grid>

            {/* Mobile helper text */}
            {isMobile && (
              <Box sx={{ 
                mt: 2, 
                p: 1.5, 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 1,
                border: '1px solid rgba(63, 81, 181, 0.3)'
              }}>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.8rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500
                }}>
                  <InfoIcon size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  <strong>Tip:</strong> Take photos first, then fill out the details below. You can always add more info later!
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Basic Information Section */}
          <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(25, 118, 210, 0.02)' }}>
            <SectionHeader 
              icon={<InfoIcon size={20} />} 
              title="Basic Information" 
              completed={isBasicComplete}
            />
            
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ 
                  fontSize: isMobile ? '0.95rem' : '0.875rem',
                  fontWeight: isMobile ? 600 : 500 
                }}>
                  Equipment Name *
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    ml: 1,
                    fontSize: isMobile ? '0.8rem' : '0.75rem'
                  }}>
                    {isMobile ? "(What should we call this?)" : "(Type any name or select from suggestions)"}
                  </Typography>
                </Typography>
                <SmartNameField
                  value={toTitleCase(newItem.name)}
                  onChange={(name, productionItem) => {
                    if (productionItem) {
                      setNewItem({
                        ...newItem,
                        name: toTitleCase(name),
                        category: productionItem.categories[0] as Category,
                        tags: [...productionItem.tags],
                        requiresPower: productionItem.requiresPower || false,
                      });
                    } else {
                      setNewItem(prev => ({
                        ...prev,
                        name: toTitleCase(name),
                        category: prev.category || 'Other' as Category,
                      }));
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={newItem.category}
                    label="Category *"
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value as Category })}
                    startAdornment={
                      <InputAdornment position="start">
                        <CategoryIcon size={20} />
                      </InputAdornment>
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: isMobile ? 300 : 400
                        }
                      }
                    }}
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat} value={cat} sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  label="Description"
                  placeholder={isMobile ? "What does this equipment do?" : "Describe the equipment's purpose and key features"}
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  helperText={isMobile ? "Optional - helps others understand what this is" : "Help others understand what this equipment does"}
                  InputProps={{
                    sx: {
                      fontSize: isMobile ? '0.9rem' : '1rem'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  placeholder={isMobile ? "Any special notes?" : "Any special handling instructions or additional information"}
                  value={newItem.notes}
                  onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                  InputProps={{
                    sx: {
                      fontSize: isMobile ? '0.9rem' : '1rem'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Location Section - Progressive reveal with mobile focus */}
          <Fade in={isBasicComplete}>
            <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(76, 175, 80, 0.02)' }}>
              <SectionHeader 
                icon={<LocationIcon size={20} />} 
                title={isMobile ? "Where to Store" : "Location Information"}
                completed={isLocationComplete}
              />
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={hasSecondLocation(newItem.primaryLocation) ? 6 : 8}>
                  <FormControl fullWidth required>
                    <InputLabel sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                      {isMobile ? "Where will this go? *" : "Where will this be stored? *"}
                    </InputLabel>
                    <Select
                      value={newItem.primaryLocation}
                      label={isMobile ? "Where will this go? *" : "Where will this be stored? *"}
                      onChange={(e) => handlePrimaryLocationChange(e.target.value as Location)}
                      startAdornment={
                        <InputAdornment position="start">
                          <LocationIcon size={20} />
                        </InputAdornment>
                      }
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: isMobile ? 250 : 400
                          }
                        }
                      }}
                    >
                      {LOCATIONS.map((loc) => (
                        <MenuItem key={loc} value={loc} sx={{ 
                          fontSize: isMobile ? '0.85rem' : '1rem',
                          py: isMobile ? 1.5 : 1
                        }}>
                          {loc}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Second Location - Progressive reveal */}
                {hasSecondLocation(newItem.primaryLocation) && (
                  <Fade in={true}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                          {isMobile ? "Which shelf?" : "Shelf/Drawer"}
                        </InputLabel>
                        <Select
                          value={newItem.secondLocation || ''}
                          label={isMobile ? "Which shelf?" : "Shelf/Drawer"}
                          onChange={(e) => setNewItem({ 
                            ...newItem, 
                            secondLocation: e.target.value as SecondLocation 
                          })}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: isMobile ? 200 : 300
                              }
                            }
                          }}
                        >
                          <MenuItem value="" sx={{ 
                            fontSize: isMobile ? '0.85rem' : '1rem',
                            py: isMobile ? 1.5 : 1
                          }}>
                            <em>None specified</em>
                          </MenuItem>
                          {availableSecondLocations.map((loc) => (
                            <MenuItem key={loc} value={loc} sx={{ 
                              fontSize: isMobile ? '0.85rem' : '1rem',
                              py: isMobile ? 1.5 : 1
                            }}>
                              {loc}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Fade>
                )}

                <Grid item xs={12} md={hasSecondLocation(newItem.primaryLocation) ? 12 : 4}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                      {isMobile ? "Backup location?" : "Alternative Location"}
                    </InputLabel>
                    <Select
                      value={newItem.secondaryLocation || ''}
                      label={isMobile ? "Backup location?" : "Alternative Location"}
                      onChange={(e) => setNewItem({ 
                        ...newItem, 
                        secondaryLocation: e.target.value as Location | undefined 
                      })}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: isMobile ? 250 : 400
                          }
                        }
                      }}
                    >
                      <MenuItem value="" sx={{ 
                        fontSize: isMobile ? '0.85rem' : '1rem',
                        py: isMobile ? 1.5 : 1
                      }}>
                        <em>No backup location</em>
                      </MenuItem>
                      {availableSecondaryLocations.map((loc) => (
                        <MenuItem 
                          key={loc} 
                          value={loc}
                          disabled={loc === newItem.primaryLocation}
                          sx={{ 
                            fontSize: isMobile ? '0.85rem' : '1rem',
                            py: isMobile ? 1.5 : 1
                          }}
                        >
                          {loc}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {/* Mobile location helper */}
              {isMobile && isLocationComplete && (
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: 'rgba(76, 175, 80, 0.15)', 
                  borderRadius: 1,
                  border: '1px solid rgba(76, 175, 80, 0.4)'
                }}>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.8rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 500
                  }}>
                    <CheckCircleIcon size={14} style={{ marginRight: '6px', verticalAlign: 'middle', color: '#4CAF50' }} />
                    <strong>Great!</strong> This equipment will be stored in <strong>{newItem.primaryLocation}</strong>
                    {newItem.secondLocation && (
                      <> on <strong>{newItem.secondLocation}</strong></>
                    )}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Fade>

          {/* Purchase Information Section - Collapsible with mobile focus */}
          {isLocationComplete && (
            <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(255, 152, 0, 0.02)' }}>
              <SectionHeader 
                icon={<BusinessIcon size={20} />} 
                title={isMobile ? "Purchase Info (Optional)" : "Purchase Information (Optional)"} 
                completed={hasPurchaseInfo}
                onClick={() => setShowAdvanced(!showAdvanced)}
                collapsible={true}
              />
              
              {isMobile && !showAdvanced && (
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block',
                  mt: 1,
                  fontSize: '0.8rem',
                  fontStyle: 'italic'
                }}>
                  Tap here to add purchase details, pricing, vendor info...
                </Typography>
              )}
              
              <Collapse in={showAdvanced}>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={isMobile ? 2 : 3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Purchase Date"
                        value={newItem.purchaseDate}
                        onChange={(e) => setNewItem({ ...newItem, purchaseDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Calendar size={20} />
                            </InputAdornment>
                          ),
                          sx: {
                            fontSize: isMobile ? '0.9rem' : '1rem'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        freeSolo
                        options={VENDORS}
                        value={newItem.vendor || ''}
                        onChange={(_, newValue) => setNewItem({ 
                          ...newItem, 
                          vendor: newValue as Vendor | undefined 
                        })}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Vendor" 
                            placeholder={isMobile ? "Who sold this?" : "Who did you buy this from?"}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BusinessIcon size={20} />
                                </InputAdornment>
                              ),
                              sx: {
                                fontSize: isMobile ? '0.9rem' : '1rem'
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Purchase Value"
                        placeholder="0.00"
                        value={newItem.purchaseValue || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          setNewItem({ 
                            ...newItem, 
                            purchaseValue: value,
                            currentValue: value,
                            isInsured: value >= 500
                          });
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Euro size={20} />
                            </InputAdornment>
                          ),
                          sx: {
                            fontSize: isMobile ? '0.9rem' : '1rem'
                          }
                        }}
                        helperText={isMobile ? "€500+ auto-insured" : "Items over €500 are automatically insured"}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Current Value"
                        placeholder="0.00"
                        value={newItem.currentValue || ''}
                        onChange={(e) => setNewItem({ 
                          ...newItem, 
                          currentValue: parseFloat(e.target.value) 
                        })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Euro size={20} />
                            </InputAdornment>
                          ),
                          sx: {
                            fontSize: isMobile ? '0.9rem' : '1rem'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {(newItem.purchaseValue || 0) >= 500 && (
                        <Box sx={{ pt: isMobile ? 1 : 2 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={newItem.isInsured}
                                onChange={(e) => setNewItem({ ...newItem, isInsured: e.target.checked })}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                Insurance Coverage
                              </Typography>
                            }
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* Technical Specifications - Collapsible with mobile focus */}
          {isLocationComplete && (
            <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(156, 39, 176, 0.02)' }}>
              <SectionHeader 
                icon={<SettingsIcon size={20} />} 
                title={isMobile ? "Technical Details (Optional)" : "Technical Specifications (Optional)"} 
                completed={hasTechnicalInfo}
                onClick={() => setShowTechnical(!showTechnical)}
                collapsible={true}
              />
              
              {isMobile && !showTechnical && (
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block',
                  mt: 1,
                  fontSize: '0.8rem',
                  fontStyle: 'italic'
                }}>
                  Tap here to add serial numbers, power requirements, tags...
                </Typography>
              )}
              
              <Collapse in={showTechnical}>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={isMobile ? 2 : 3}>
                    {/* Serial Number - Progressive reveal based on category */}
                    {shouldShowSerialNumber(newItem.category) && (
                      <Fade in={true}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Serial Number"
                            placeholder={isMobile ? "Enter S/N" : "Enter serial number"}
                            value={newItem.serialNumber || ''}
                            onChange={(e) => handleSerialNumberChange(e.target.value)}
                            helperText={isMobile ? "Auto-uppercase" : "Automatically converted to uppercase"}
                            InputProps={{
                              sx: {
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                '& input': {
                                  fontFamily: 'monospace',
                                  textTransform: 'uppercase'
                                }
                              }
                            }}
                          />
                        </Grid>
                      </Fade>
                    )}

                    {/* Power Requirements */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ pt: isMobile ? 1 : 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={newItem.requiresPower}
                              onChange={(e) => setNewItem({ ...newItem, requiresPower: e.target.checked })}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                              {isMobile ? "Needs Power" : "Requires Power Connection"}
                            </Typography>
                          }
                        />
                      </Box>
                    </Grid>

                    {/* Power Location - Progressive reveal */}
                    {newItem.requiresPower && (
                      <Fade in={true}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                              {isMobile ? "Power from where?" : "Power Source Location"}
                            </InputLabel>
                            <Select
                              value={newItem.powerLocation || ''}
                              label={isMobile ? "Power from where?" : "Power Source Location"}
                              onChange={(e) => setNewItem({ ...newItem, powerLocation: e.target.value as Location })}
                              startAdornment={
                                <InputAdornment position="start">
                                  <Power size={20} />
                                </InputAdornment>
                              }
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: isMobile ? 250 : 400
                                  }
                                }
                              }}
                            >
                              {LOCATIONS.map((loc) => (
                                <MenuItem key={loc} value={loc} sx={{ 
                                  fontSize: isMobile ? '0.85rem' : '1rem',
                                  py: isMobile ? 1.5 : 1
                                }}>
                                  {loc}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Fade>
                    )}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Quantity"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) })}
                        inputProps={{ min: 1 }}
                        helperText={isMobile ? "How many?" : "How many of this item do you have?"}
                        InputProps={{
                          sx: {
                            fontSize: isMobile ? '0.9rem' : '1rem'
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={existingTags}
                        value={newItem.tags}
                        onChange={(_, newValue) => setNewItem({ 
                          ...newItem, 
                          tags: newValue 
                        })}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tags"
                            placeholder={isMobile ? "Add tags..." : "wireless, backup, stage-left, etc."}
                            helperText={isMobile ? "Type tags and press Enter" : "Select existing tags or add new ones"}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <TagIcon size={20} />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                              sx: {
                                fontSize: isMobile ? '0.9rem' : '1rem'
                              }
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              size={isMobile ? "small" : "medium"}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: isMobile ? 2 : 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        {isMobile ? (
          <>
            <Button 
              onClick={handleSave}
              variant="contained"
              disabled={!newItem.name?.trim() || !newItem.category || !newItem.primaryLocation}
              fullWidth
              size="large"
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 3,
                backgroundColor: '#4CAF50',
                '&:hover': {
                  backgroundColor: '#45a049',
                  boxShadow: 4
                }
              }}
            >
              Add Equipment
            </Button>
            <Button 
              onClick={onClose}
              fullWidth
              size="large"
              sx={{
                py: 1.5,
                fontSize: '0.95rem',
                color: 'text.secondary'
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button 
              onClick={handleSave}
              variant="contained"
              disabled={!newItem.name?.trim() || !newItem.category || !newItem.primaryLocation}
            >
              Add Equipment
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddItemDialog;